import { ensureFreshAuth, getAccessToken, getIssuer, refreshAuth } from './auth';
import { CHAT_MESSAGE_MAX_CHARS, buildMessageTooLongError } from './chat-limits';
import type {
  AccountStatusResponse,
  ChatResponse,
  ConversationItem,
  ConversationMessage,
  ConversationUpdatePayload,
  DocumentsCapabilitiesResponse,
  DocumentsJobResponse,
  DocumentsListResponse,
  DocumentsUploadResponse,
  FavoriteItem,
  HistoryItem,
  LawNode,
  LawType,
  LinkAccountResponse,
  LinkByCodeResponse,
  ProjectToolsPayload,
  SearchResult,
  SpaceItem,
  TreeNode,
  UserPlanResponse,
  UserProfile,
} from './api-types';

const AUTH_API_BASE = import.meta.env.VITE_AUTH_API_URL || '/auth-api';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'

type ValidationErrorItem = {
  type?: string
  loc?: Array<string | number>
  msg?: string
  ctx?: Record<string, unknown>
}

function parseApiErrorBody(rawBody: string): { detailText: string; validationItems: ValidationErrorItem[] } {
  const trimmed = String(rawBody || '').trim()
  if (!trimmed) {
    return { detailText: '', validationItems: [] }
  }
  try {
    const parsed = JSON.parse(trimmed)
    const detail = (parsed as { detail?: unknown }).detail
    if (Array.isArray(detail)) {
      return {
        detailText: '',
        validationItems: detail as ValidationErrorItem[],
      }
    }
    if (typeof detail === 'string') {
      return { detailText: detail, validationItems: [] }
    }
    return { detailText: trimmed, validationItems: [] }
  } catch {
    return { detailText: trimmed, validationItems: [] }
  }
}

function resolveFriendlyApiError(status: number, rawBody: string): string {
  const { detailText, validationItems } = parseApiErrorBody(rawBody)
  const details = validationItems.filter((item) => item && typeof item === 'object')
  const messageLengthIssue = details.find((item) => {
    const loc = Array.isArray(item.loc) ? item.loc.map((part) => String(part)).join('.') : ''
    return String(item.type || '').toLowerCase() === 'string_too_long' && loc.includes('message')
  })
  if (messageLengthIssue) {
    const maxLength = Number((messageLengthIssue.ctx || {}).max_length || CHAT_MESSAGE_MAX_CHARS)
    return buildMessageTooLongError(Number.isFinite(maxLength) ? maxLength : CHAT_MESSAGE_MAX_CHARS)
  }

  if (status === 422 && details.length > 0) {
    const firstText = details
      .map((item) => String(item.msg || '').trim())
      .find(Boolean)
    if (firstText) return `Ошибка в параметрах запроса: ${firstText}`
    return 'Ошибка в параметрах запроса.'
  }

  if (detailText) {
    const compact = detailText.replace(/\s+/g, ' ').trim()
    return compact.length > 500 ? `${compact.slice(0, 497)}...` : compact
  }
  return `API Error: ${status}`
}

async function doFetch(endpoint: string, options?: RequestInit): Promise<Response> {
  if (getAccessToken()) {
    await ensureFreshAuth(30).catch(() => false);
  }
  const accessToken = getAccessToken();
  const issuer = getIssuer();
  const isFormDataBody = typeof FormData !== 'undefined' && options?.body instanceof FormData

  const headers: Record<string, string> = {};
  if (!isFormDataBody) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  if (issuer) {
    headers['X-OIDC-Issuer'] = issuer;
  }

  return fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options?.headers,
    },
  });
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  let response = await doFetch(endpoint, options);

  if (response.status === 401) {
    const refreshed = await refreshAuth();
    if (refreshed) {
      response = await doFetch(endpoint, options);
    }
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(resolveFriendlyApiError(response.status, errorBody));
  }

  return response.json();
}

export const apiClient = {
  // Актуальный список LLM-моделей (единый с веб-кабинетом и VK): GET /api/v1/models.
  // Не бросает — при ошибке возвращаем [] (SettingsProvider оставит фолбэк).
  getModels: async (): Promise<Array<{ id: string; name: string; description: string; cost?: string }>> => {
    try {
      return await request<Array<{ id: string; name: string; description: string; cost?: string }>>('/models')
    } catch {
      return []
    }
  },

  // Law types and tree
  getLawTypes: () => request<LawType[]>('/law/types'),
  
  getLawTree: (lawCode: string, parentId?: string) => {
    const params = parentId ? `?parent_id=${parentId}` : ''
    return request<TreeNode[]>(`/law/tree/${lawCode}${params}`)
  },
  
  getLawNode: (lawCode: string, nodeId: string) =>
    request<LawNode>(`/law/node/${lawCode}/${nodeId}`),

  // Разрешить статью по (код кодекса, номер) → node_id (для цитат без node_id).
  resolveArticle: (lawCode: string, article: string) =>
    request<{ item?: { node_id?: string; canonical_url?: string; law_code?: string } }>(
      `/law/article/${encodeURIComponent(lawCode)}/${encodeURIComponent(article)}`,
    ),

  // Search
  search: async (query: string, lawCode?: string): Promise<SearchResult[]> => {
    const params = new URLSearchParams({ q: query })
    if (lawCode) params.append('law_code', lawCode)
    const res = await request<{ query: string; items: SearchResult[]; total: number }>(`/search?${params}`)
    return res.items
  },

  // Favorites
  getFavorites: () => request<FavoriteItem[]>('/favorites'),
  
  addFavorite: (lawCode: string, nodeId: string, title?: string) =>
    request<FavoriteItem>('/favorites', {
      method: 'POST',
      body: JSON.stringify({ law_code: lawCode, node_id: nodeId, title }),
    }),
  
  removeFavorite: (nodeId: string) =>
    request<void>(`/favorites/${nodeId}`, { method: 'DELETE' }),
  
  checkFavorite: async (nodeId: string): Promise<boolean> => {
    try {
      const res = await request<{ is_favorite: boolean }>(`/favorites/${nodeId}/check`)
      return res.is_favorite
    } catch {
      return false
    }
  },

  // User plan & profile
  getUserPlan: () => request<UserPlanResponse>('/user/plan'),
  getUserProfile: () => request<UserProfile>('/user/profile'),

  // Тарифы + покупка ВНУТРИ мини-аппа (без входа на веб). Премиум привязывается к этому аккаунту.
  getTariffs: () => request<{ service: string; current_plan: string; active: boolean; period_end: string | null; plans: Array<{ plan_id: string; title: string; description: string; amount_rub: number; period_days: number; is_current: boolean }> }>('/billing/tariffs'),
  createCheckout: (planId: string, returnPath?: string) => request<{ confirmation_url?: string; payment_id?: string; amount_rub?: number; status?: string }>('/billing/checkout', { method: 'POST', body: JSON.stringify({ plan_id: planId, product: 'law', return_path: returnPath }) }),

  // Conversations
  getConversations: (limit = 30, spaceId?: string) => {
    const params = new URLSearchParams({ limit: String(limit) })
    if (spaceId) params.set('space_id', spaceId)
    return request<{ items: ConversationItem[]; total: number }>(`/conversations?${params.toString()}`)
  },
  createConversation: (
    title?: string,
    selectedDocIds?: string[],
    options?: { spaceId?: string; contextMode?: 'isolated' | 'space_shared' },
  ) =>
    request<{
      id: string
      title: string
      space_id?: string | null
      context_mode?: 'isolated' | 'space_shared'
      branch_parent_conversation_id?: string | null
      branch_from_message_id?: number | null
      model?: string
      selected_doc_ids?: string[] | null
    }>('/conversations', {
      method: 'POST',
      body: JSON.stringify({
        title,
        selected_doc_ids: selectedDocIds,
        space_id: options?.spaceId,
        context_mode: options?.contextMode,
      }),
    }),
  branchConversation: (
    conversationId: string,
    payload?: { fromMessageId?: number; title?: string },
  ) =>
    request<{
      id: string
      title: string
      space_id?: string | null
      context_mode?: 'isolated' | 'space_shared'
      branch_parent_conversation_id?: string | null
      branch_from_message_id?: number | null
      model?: string
      selected_doc_ids?: string[] | null
    }>(`/conversations/${encodeURIComponent(conversationId)}/branch`, {
      method: 'POST',
      body: JSON.stringify({
        from_message_id: payload?.fromMessageId,
        title: payload?.title,
      }),
    }),
  updateConversation: (conversationId: string, payload: ConversationUpdatePayload) =>
    request<{ id: string; title: string; selected_doc_ids?: string[] | null }>(
      `/conversations/${encodeURIComponent(conversationId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    ),
  getConversationMessages: (conversationId: string, limit = 50) =>
    request<{ messages: ConversationMessage[] }>(`/conversations/${conversationId}/messages?limit=${limit}`),
  deleteConversation: (conversationId: string) =>
    request<{ deleted: boolean; id: string }>(`/conversations/${conversationId}`, { method: 'DELETE' }),

  // Chat with agent
  sendChatMessage: (
    message: string,
    conversationId?: string,
    model?: string,
    selectedDocIds?: string[],
    options?: {
      spaceId?: string
      contextMode?: 'isolated' | 'space_shared'
      webSearchMode?: 'off' | 'auto' | 'on'
    },
  ) =>
    request<ChatResponse>('/chat', {
      method: 'POST',
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
        model,
        selected_doc_ids: selectedDocIds,
        space_id: options?.spaceId,
        context_mode: options?.contextMode,
        web_search_mode: options?.webSearchMode,
      }),
    }),

  streamChat: async function* (
    message: string,
    conversationId?: string,
    model?: string,
    selectedDocIds?: string[],
    options?: {
      spaceId?: string
      contextMode?: 'isolated' | 'space_shared'
      webSearchMode?: 'off' | 'auto' | 'on'
    },
  ) {
    const fetchOpts = {
      method: 'POST',
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
        model,
        selected_doc_ids: selectedDocIds,
        space_id: options?.spaceId,
        context_mode: options?.contextMode,
        web_search_mode: options?.webSearchMode,
      }),
    }
    let response = await doFetch('/chat/stream', fetchOpts)
    if (response.status === 401) {
      const refreshed = await refreshAuth()
      if (refreshed) {
        response = await doFetch('/chat/stream', fetchOpts)
      }
    }
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(resolveFriendlyApiError(response.status, text))
    }
    const reader = response.body?.getReader()
    if (!reader) return
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6).trim()
        if (data === '[DONE]') return
        try {
          yield JSON.parse(data)
        } catch { /* skip malformed */ }
      }
    }
  },

  // Reattach to an in-progress agent run for a conversation (after navigating away
  // and back). The backend replays the live tail from its run-store; emits
  // {type:"no_active_run"} immediately if nothing is running. Same SSE shape as streamChat.
  reattachRun: async function* (conversationId: string) {
    const fetchOpts = { method: 'GET' }
    const path = `/conversations/${conversationId}/run-stream`
    let response = await doFetch(path, fetchOpts)
    if (response.status === 401) {
      const refreshed = await refreshAuth()
      if (refreshed) response = await doFetch(path, fetchOpts)
    }
    if (!response.ok) return
    const reader = response.body?.getReader()
    if (!reader) return
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6).trim()
        if (data === '[DONE]') return
        try {
          yield JSON.parse(data)
        } catch { /* skip malformed */ }
      }
    }
  },

  // Account linking (routed via AUTH Service, not bot-service)
  getAccountStatus: async (): Promise<AccountStatusResponse> => {
    const accessToken = getAccessToken();
    const issuer = getIssuer();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    if (issuer) headers['X-OIDC-Issuer'] = issuer;
    let resp = await fetch(`${AUTH_API_BASE}/api/v1/telegram/mini-app/account-status`, { headers });
    if (resp.status === 401) {
      const ok = await refreshAuth();
      if (ok) {
        const newToken = getAccessToken();
        if (newToken) headers['Authorization'] = `Bearer ${newToken}`;
        resp = await fetch(`${AUTH_API_BASE}/api/v1/telegram/mini-app/account-status`, { headers });
      }
    }
    if (!resp.ok) throw new Error(`API Error: ${resp.status}`);
    return resp.json();
  },
  linkAccount: async (email: string, password: string): Promise<LinkAccountResponse> => {
    const accessToken = getAccessToken();
    const issuer = getIssuer();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    if (issuer) headers['X-OIDC-Issuer'] = issuer;
    let resp = await fetch(`${AUTH_API_BASE}/api/v1/telegram/mini-app/link-account`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, password }),
    });
    if (resp.status === 401) {
      const ok = await refreshAuth();
      if (ok) {
        const newToken = getAccessToken();
        if (newToken) headers['Authorization'] = `Bearer ${newToken}`;
        resp = await fetch(`${AUTH_API_BASE}/api/v1/telegram/mini-app/link-account`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ email, password }),
        });
      }
    }
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(body.detail || `API Error: ${resp.status}`);
    }
    return resp.json();
  },
  // Fold THIS mini-app account into a pre-existing web account using a one-time
  // code generated in the web cabinet. On success the caller should reload the
  // app: re-running init with the same Telegram initData now resolves to the
  // linked web account (chats / documents / premium become shared).
  linkByCode: async (code: string): Promise<LinkByCodeResponse> => {
    const accessToken = getAccessToken();
    const issuer = getIssuer();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    if (issuer) headers['X-OIDC-Issuer'] = issuer;
    const payload = JSON.stringify({ code: code.trim(), platform: 'telegram' });
    let resp = await fetch(`${AUTH_API_BASE}/api/v1/telegram/mini-app/link-by-code`, {
      method: 'POST',
      headers,
      body: payload,
    });
    if (resp.status === 401) {
      const ok = await refreshAuth();
      if (ok) {
        const newToken = getAccessToken();
        if (newToken) headers['Authorization'] = `Bearer ${newToken}`;
        resp = await fetch(`${AUTH_API_BASE}/api/v1/telegram/mini-app/link-by-code`, {
          method: 'POST',
          headers,
          body: payload,
        });
      }
    }
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(body.detail || `API Error: ${resp.status}`);
    }
    return resp.json();
  },

  // History
  getHistory: () => request<HistoryItem[]>('/history'),

  // Spaces
  // NOTE: the miniapp still uses "space" naming internally for compatibility,
  // but backend endpoints are canonicalized to /projects for web+miniapp sync.
  getSpaces: (limit = 50) =>
    request<{ items: SpaceItem[]; total: number }>(`/projects?limit=${limit}&offset=0`),
  createSpace: (payload: {
    name: string
    description?: string
    settings?: Record<string, unknown>
  }) =>
    request<SpaceItem>('/projects', {
      method: 'POST',
      body: JSON.stringify({
        name: payload.name,
        description: payload.description,
        visibility: 'private',
        settings: payload.settings || {},
      }),
    }),
  updateSpace: (spaceId: string, payload: {
    name?: string
    description?: string
    settings?: Record<string, unknown>
  }) =>
    request<SpaceItem>(`/projects/${encodeURIComponent(spaceId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteSpace: (spaceId: string) =>
    request<{ deleted: boolean; id: string }>(`/projects/${encodeURIComponent(spaceId)}`, { method: 'DELETE' }),
  getProjectTools: (projectId: string) =>
    request<ProjectToolsPayload>(`/projects/${encodeURIComponent(projectId)}/tools`),
  updateProjectTools: (
    projectId: string,
    payload: {
      links?: string[]
      instructions?: string
      tasks?: string[]
      notes?: string
    },
  ) =>
    request<ProjectToolsPayload>(`/projects/${encodeURIComponent(projectId)}/tools`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  // Documents
  getDocuments: (params?: {
    conversationId?: string
    contractId?: string
    page?: number
    pageSize?: number
  }) => {
    const query = new URLSearchParams()
    if (params?.conversationId) query.set('conversation_id', params.conversationId)
    if (params?.contractId) query.set('contract_id', params.contractId)
    query.set('page', String(params?.page || 1))
    query.set('page_size', String(params?.pageSize || 200))
    return request<DocumentsListResponse>(`/documents?${query.toString()}`)
  },

  getDocumentsCapabilities: () =>
    request<DocumentsCapabilitiesResponse>('/documents/capabilities'),

  uploadDocuments: (files: File[], contractId?: string) => {
    const form = new FormData()
    files.forEach((file) => form.append('files', file))
    if (contractId) form.append('contract_id', contractId)
    const headers: Record<string, string> = {}
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      headers['Idempotency-Key'] = crypto.randomUUID()
    } else {
      headers['Idempotency-Key'] = `${Date.now()}-${Math.random()}`
    }
    return request<DocumentsUploadResponse>('/documents/upload', {
      method: 'POST',
      headers,
      body: form,
    })
  },

  uploadChatDocuments: (files: File[], conversationId: string, contractId?: string) => {
    const form = new FormData()
    files.forEach((file) => form.append('files', file))
    form.append('conversation_id', conversationId)
    if (contractId) form.append('contract_id', contractId)
    const headers: Record<string, string> = {}
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      headers['Idempotency-Key'] = crypto.randomUUID()
    } else {
      headers['Idempotency-Key'] = `${Date.now()}-${Math.random()}`
    }
    return request<DocumentsUploadResponse>('/chat/upload', {
      method: 'POST',
      headers,
      body: form,
    })
  },

  getDocumentsJob: (jobId: string) =>
    request<DocumentsJobResponse>(`/documents/jobs/${encodeURIComponent(jobId)}`),

  deleteDocument: (docId: string) => {
    const headers: Record<string, string> = {}
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      headers['Idempotency-Key'] = crypto.randomUUID()
    } else {
      headers['Idempotency-Key'] = `${Date.now()}-${Math.random()}`
    }
    return request<{ item: { deleted: boolean; doc_id: string } }>(`/documents/${encodeURIComponent(docId)}`, {
      method: 'DELETE',
      headers,
    })
  },
}
